import { GraduationCap } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";
import AIReadabilityBlock from "@/components/AIReadabilityBlock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const StudentVisas = () => {
  const faqs = [
    {
      question: "What's the difference between F-1 and M-1 visas?",
      answer: "F-1 visas are for academic studies at colleges, universities, high schools, or language schools. M-1 visas are for vocational or technical training. F-1 students have more work authorization options (CPT, OPT) than M-1 students.",
    },
    {
      question: "Can I work on a student visa?",
      answer: "F-1 students can work on-campus part-time and, after one year, may be eligible for Curricular Practical Training (CPT) and Optional Practical Training (OPT). OPT allows you to work for up to 12 months (36 months for STEM fields) after graduation.",
    },
  ];

  const mainContent = (
    <>
      <div className="prose prose-lg max-w-none mb-12">
        <h2 className="text-3xl font-bold mb-6">Study, Grow, and Succeed in America</h2>
        <p className="text-xl text-muted-foreground mb-8">
          The United States is home to some of the world's top universities, colleges, and academic institutions. If you're an international student looking to pursue your education in the U.S., a student visa is your key to unlocking those opportunities.
        </p>

        <h3 className="text-2xl font-bold mb-4">Types of Student Visas</h3>
        <ul className="list-disc pl-6 mb-6">
          <li><strong>F-1 Visa:</strong> Academic studies at accredited institutions</li>
          <li><strong>M-1 Visa:</strong> Vocational or technical training programs</li>
          <li><strong>J-1 Visa:</strong> Exchange visitor programs (students, researchers, teachers)</li>
        </ul>

        <h3 className="text-2xl font-bold mb-4">F-1 Visa Process</h3>
        <ol className="list-decimal pl-6 mb-6">
          <li>Obtain acceptance from SEVP-approved school</li>
          <li>Receive Form I-20 from your school</li>
          <li>Pay SEVIS I-901 fee</li>
          <li>Complete DS-160 visa application</li>
          <li>Schedule and attend visa interview at U.S. embassy/consulate</li>
          <li>Receive visa and travel to U.S.</li>
        </ol>

        <h3 className="text-2xl font-bold mb-4">Maintaining Student Status</h3>
        <ul className="list-disc pl-6 mb-6">
          <li>Maintain full-time enrollment</li>
          <li>Make normal academic progress</li>
          <li>Don't work without authorization</li>
          <li>Keep passport and I-20 valid</li>
          <li>Report address changes within 10 days</li>
        </ul>

        <h3 className="text-2xl font-bold mb-4">After Graduation Options</h3>
        <ul className="list-disc pl-6 mb-8">
          <li>Optional Practical Training (OPT)</li>
          <li>STEM OPT Extension (24 months additional)</li>
          <li>H-1B work visa sponsorship</li>
          <li>Change to other visa categories</li>
        </ul>

        <h3 className="text-2xl font-bold mb-6">Frequently Asked Questions</h3>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );

  const aiKeyPoints = [
    "F-1 student visa for academic studies",
    "M-1 visa for vocational and technical training",
    "J-1 exchange visitor visas",
    "I-20 form assistance and school transfers",
    "SEVIS compliance and status maintenance",
    "Optional Practical Training (OPT) applications",
    "STEM OPT 24-month extension",
    "Curricular Practical Training (CPT)",
    "Change of status to work visas (H-1B)"
  ];

  const aiFaqs = faqs.map(f => ({ question: f.question, answer: f.answer }));

  return (
    <>
      <AIReadabilityBlock
        serviceName="U.S. Student Visa Services"
        serviceDescription="Student visa attorney in Houston helping international students with F-1, M-1, and J-1 visas, OPT applications, STEM extensions, and transitions to work visas."
        keyPoints={aiKeyPoints}
        faqs={aiFaqs}
        areaServed="Houston, Texas"
      />
      <GenericPracticePage
        title="U.S. Student Visas"
        subtitle="F-1, M-1, and J-1 visas for international students"
        icon={<GraduationCap className="h-20 w-20 mx-auto mb-6 text-accent" />}
        mainContent={mainContent}
      />
    </>
  );
};

export default StudentVisas;
